/**
 * Build Breakdown State from Extraction Result
 * 
 * Transforms the raw API extraction response into a UI-ready
 * BreakdownState with field states, derived lists, readiness,
 * and resolution queue metadata.
 */

import type { ExtractionFieldData } from '@/components/intake/ExtractionField';
import type { ExtractionResult } from '@/components/intake/VisibleBreakdownScreen';
import { computeBreakdownReadiness, type BreakdownReadiness } from './computeBreakdownReadiness';

// ── Types ──

export interface BreakdownFieldState extends ExtractionFieldData {
  fieldName: string;
  isCritical: boolean;
  isRequiredToProceed: boolean;
  isEditing: boolean;
  editDraft: string | number | null;
  hasValidationError: boolean;
  validationError: string | null;
  touched: boolean;
}

export interface EditableListItem {
  id: string;
  value: string;
  source: 'llm' | 'document' | 'merged' | 'user';
  confidence: number;
  confirmation_status: 'unreviewed' | 'accepted' | 'edited' | 'confirmed';
  source_snippet: string | null;
  isEditing: boolean;
  editDraft: string;
}

export interface ConflictItem {
  field_name: string;
  reason: string;
  source_a?: string | null;
  source_b?: string | null;
}

export type BreakdownBlockKey = 'dilemma' | 'decisionCore' | 'assumptions' | 'risks' | 'classification' | 'missing';

export type InputQualityLevel = 'high' | 'medium' | 'low';

export interface BreakdownState {
  fields: Record<string, BreakdownFieldState>;
  derived: {
    coreAssumptions: EditableListItem[];
    keyRisks: EditableListItem[];
  };
  conflicts: ConflictItem[];
  missingFields: string[];
  readiness: BreakdownReadiness;
  dilemmaConfirmed: boolean;
  inputQuality: InputQualityLevel;
}

// ── Constants ──

const HARD_REQUIRED = ['optionA', 'optionB', 'decisionType', 'impactScore', 'urgencyScore'];
const CRITICAL_FIELDS = [...HARD_REQUIRED, 'initialPreference'];

// ── Builder ──

function toBreakdownField(fieldName: string, field: ExtractionFieldData): BreakdownFieldState {
  return {
    ...field,
    fieldName,
    isCritical: CRITICAL_FIELDS.includes(fieldName),
    isRequiredToProceed: HARD_REQUIRED.includes(fieldName),
    isEditing: false,
    editDraft: null,
    hasValidationError: false,
    validationError: null,
    touched: false,
  };
}

function buildEditableList(
  items: string[],
  source: 'llm' | 'document' = 'llm',
): EditableListItem[] {
  return items.map((value, i) => ({
    id: `${source}-${i}-${Date.now()}`,
    value,
    source,
    confidence: 0.7,
    confirmation_status: 'unreviewed' as const,
    source_snippet: null,
    isEditing: false,
    editDraft: value,
  }));
}

export function buildBreakdownState(extraction: ExtractionResult, inputQuality?: InputQualityLevel): BreakdownState {
  const fields: Record<string, BreakdownFieldState> = {};

  for (const [key, fieldData] of Object.entries(extraction.fields)) {
    fields[key] = toBreakdownField(key, fieldData);
  }

  // Ensure all hard-required fields exist in state even if missing from extraction
  for (const fieldName of HARD_REQUIRED) {
    if (!fields[fieldName]) {
      fields[fieldName] = toBreakdownField(fieldName, {
        value: null,
        confidence: 0,
        source: 'llm',
        detection_status: 'missing',
        confirmation_status: 'unreviewed',
        source_snippet: null,
      });
    }
  }

  const conflicts: ConflictItem[] = extraction.conflicts.map(c => ({
    field_name: c.field_name,
    reason: c.reason,
    source_a: c.source_a,
    source_b: c.source_b,
  }));

  const missingFields = [...extraction.missing_fields];

  // Build extraction-compatible fields map for readiness calc
  const extractionFields: Record<string, ExtractionFieldData> = {};
  for (const [k, v] of Object.entries(fields)) {
    extractionFields[k] = v;
  }

  const readiness = computeBreakdownReadiness(extractionFields, missingFields, conflicts);

  // Derive input quality from extraction response or default
  const quality: InputQualityLevel = inputQuality
    || (extraction as any).input_quality
    || 'medium';

  return {
    fields,
    derived: {
      coreAssumptions: buildEditableList(extraction.derived.core_assumptions),
      keyRisks: buildEditableList(extraction.derived.key_risks),
    },
    conflicts,
    missingFields,
    readiness,
    dilemmaConfirmed: false,
    inputQuality: quality,
  };
}
